import {
    csvBoxExtractRows,
    csvBoxGeneralError,
    csvBoxHasError,
    normalizeDateHeaders
} from "../../../../global/csv_box_helpers";
import {ReactElement, useContext, useState} from "react";
import {AuthContext} from "../../../../contexts/AuthContext";
import {
    useGenerateLineItemsManagementFileUploadUrlMutation,
    useUploadLineItemsManagementFileWithStatusMutation
} from "../../../../__generated__/generated_types";
import {toast} from "react-toastify";
import {CSVBoxButton} from "../../../../components/csvbox/CSVBoxButton";
import {Button} from "@zendeskgarden/react-buttons";

interface LineItemData {
    propertyName: string;
    accountName: string;
    accountNumber: string;
    description: string;
    editOnly: boolean;
    readOnly: boolean;
    [k: string]: string | boolean;
}

function extractData(values: unknown[]): LineItemData[] {
    const retVals: LineItemData[] = [];
    for(const value of values) {
        if(typeof value !== "object" || value === null) {
            continue;
        }

        const row = value as Record<string, unknown>;
        const unmappedData = row["_unmapped_data"] as Record<string, unknown> | undefined;

        if(typeof unmappedData !== "object" || unmappedData === null || unmappedData === undefined) {
            continue;
        }

        const propertyName = row["Property"];
        const accountName = row["Account Name"];
        const accountNumber = row["Account Number"];
        const description = row["Description"];
        const editOnly = row["Edit-Only"];
        const readOnly = row["Read-Only"];

        if(typeof propertyName !== "string") {
            continue;
        }

        if(typeof accountName !== "string") {
            continue;
        }

        if(typeof accountNumber !== "string") {
            continue;
        }

        if(typeof description !== "string") {
            continue;
        }

        const normalizedDateHeaders: Record<string, string> = {};
        for(const [header, value] of Object.entries(unmappedData)) {
            const normalized = normalizeDateHeaders(header);
            if(normalized !== null) {
                normalizedDateHeaders[normalized] = String(value);
            }
        }

        retVals.push({
            propertyName: propertyName,
            accountName: accountName,
            accountNumber: accountNumber,
            description: description,
            editOnly: typeof editOnly === "string" && editOnly.trim() !== "",
            readOnly: typeof readOnly === "string" && readOnly.trim() !== "",
            ...normalizedDateHeaders
        });
    }

    return retVals;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface LineItemManagementUploadButtonProps {
}

export function LineItemManagementUploadButton(_props: LineItemManagementUploadButtonProps): ReactElement {
    const {user, budgetYear} = useContext(AuthContext);

    const [generateUploadUrl] = useGenerateLineItemsManagementFileUploadUrlMutation({
        notifyOnNetworkStatusChange: true,
    });

    const [uploadFile] = useUploadLineItemsManagementFileWithStatusMutation({
        notifyOnNetworkStatusChange: true,
    });

    const [saving, setSaving] = useState<boolean>(false);

    function doUpload(fileKey: string, signedUrl: string, data: LineItemData[]): void {
        if(!data) {
            return;
        }

        const encoder = new TextEncoder();
        const encoded = encoder.encode(JSON.stringify(data));

        fetch(signedUrl, {
            method: 'PUT',
            body: encoded.buffer,
            headers: {
                'Content-Type': "application/json",
            }
        })
                .then(() => {
                    return uploadFile({
                        variables: {
                            fileKey: fileKey,
                            budgetYear: budgetYear
                        }
                    });
                })
                .then(res => {
                    if (res.data?.uploadLineItemsManagementFileWithStatus) {
                        const resultsData = res.data.uploadLineItemsManagementFileWithStatus;
                        const {warnings, errors} = resultsData;

                        if (warnings.length > 0 || errors.length > 0) {
                            toast.success("File uploaded, but with some issues");
                        } else {
                            toast.success("File uploaded successfully");
                        }
                    } else {
                        toast.error("Error processing uploaded file");
                    }
                })
                .catch(() => {
                    toast.error("Error uploading file");
                })
                .finally(() => {
                    setSaving(false);
                });
    }

    function handleSave(data: unknown): void {
        setSaving(true);
        const rows = csvBoxExtractRows(data);
        if(rows === undefined) {
            csvBoxGeneralError("Line Items");
            setSaving(false);
            return;
        }

        const extracted = extractData(rows);
        if(extracted.length === 0) {
            csvBoxGeneralError("Line Items");
            setSaving(false);
            return;
        }

        generateUploadUrl()
            .then(url => {
                const urlData = url.data;
                if(urlData?.generateLineItemsManagementFileUploadUrl) {
                    const {key, signedUrl} = JSON.parse(urlData.generateLineItemsManagementFileUploadUrl);
                    doUpload(key, signedUrl, extracted);
                    return;
                }
                toast.error("Error generating upload URL");
                setSaving(false);
            }).catch(() => {
                toast.error("Error generating upload URL");
                setSaving(false);
            });
    }

    return <CSVBoxButton
            licenseKey="sx6Tda3PrnuKB1u18L2p3LtsOPRx9H"
            user={{
                userId: user?.id ?? "unknown",
                name: user?.firstName || user?.lastName ? `${user?.firstName || ""} ${user?.lastName || ""}` : "unknown",
                email: user?.email ?? "unknown",
                clientId: user?.clientId ?? "unknown",
                clientName: user?.clientName ?? "unknown",
            }}
            environment={{
                // This gets set based on the environment to facilitate validation callbacks.
                "base_url": process.env["REACT_APP_CSV_BOX_BASE_URL"],
                "budget_year": budgetYear
            }}
            onImport={(result, data) => {
                if(!csvBoxHasError(result, data)) {
                    handleSave(data);
                } else {
                    csvBoxGeneralError("Line Items");
                }
            }}
            render={(launch, isLoading) => {
                return <Button size={"small"} onClick={launch} disabled={isLoading || saving}>Upload Line Items</Button>;
            }}
    />;
}